import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  Wallet, RefreshCw, AlertCircle, CheckCircle2, TrendingUp, Shield,
  KeyRound, ArrowRight, ArrowRightLeft,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useState } from "react";
import { http, api, errMsg } from "@/lib/api";
import { fmtUsd } from "@/lib/format";

const fetchBalances = () => http.get("/balances").then((r) => r.data);
const fetchKlines = (symbol, interval) =>
  http.get(`/klines/${symbol}`, { params: { interval, size: 96 } })
    .then((r) => r.data);

const PROXY_IP = "82.41.248.111"; // Statik UK residential proxy — borsa whitelist IP'si

const EXCHANGE_META = {
  darkex: {
    label: "DARKEX", role: "Zarar Bacağı", color: "#ff5d15",
    loginUrl: "https://www.darkex.com/en/login",
    apiHelp: `Hesap → API Yönetimi · IP whitelist: ${PROXY_IP} (proxy) veya BOŞ · READ + Futures TRADE`,
  },
  okx: {
    label: "OKX", role: "Kâr Bacağı", color: "#FFFFFF",
    loginUrl: "https://www.okx.com/account/my-api",
    apiHelp: `API Management → V5 API · Passphrase oluştur · IP whitelist: ${PROXY_IP} veya BOŞ`,
  },
  binance: {
    label: "BINANCE", role: "Kâr Bacağı", color: "#F0B90B",
    loginUrl: "https://www.binance.com/en/my/settings/api-management",
    apiHelp: `Enable Futures · IP whitelist: ${PROXY_IP} (proxy IP) veya BOŞ · Withdrawals KAPALI`,
  },
};

// Parse common Darkex/Binance/OKX error responses into a friendly Turkish line
function friendlyError(raw) {
  if (!raw) return { title: "Anahtar yok", detail: "Bu borsa için API anahtarı girilmemiş" };
  const s = String(raw);
  if (/-2015|Invalid API-key/i.test(s))
    return { title: "API key / IP / izin hatası", detail: `IP whitelist'e ${PROXY_IP} (statik proxy) ekleyin VEYA whitelist'i BOŞ bırakın. Futures izinleri açık olmalı.` };
  if (/-2014/i.test(s))
    return { title: "Anahtar formatı bozuk", detail: "Anahtarı kopyalarken boşluk girmiş olabilirsin." };
  if (/-1021|recvWindow/i.test(s))
    return { title: "Saat senkronizasyonu", detail: "Sunucu saati ile borsa saati eşleşmiyor (genelde geçici)." };
  if (/451|restricted location/i.test(s))
    return { title: "Coğrafi engel", detail: "Binance bu sunucunun IP'sini engelliyor. Proxy URL kontrolünü yapın." };
  if (/403|Forbidden/i.test(s))
    return { title: "IP yasaklı veya yetki yok", detail: "API key'in IP whitelist'i veya Futures izni eksik olabilir." };
  if (/Anahtarlar.*yok|API anahtar/i.test(s))
    return { title: "Anahtar yok", detail: "Ayarlar'dan key + secret gir." };
  if (/PROXY_URL/i.test(s))
    return { title: "Proxy yapılandırması yok", detail: "BINANCE_PROXY_URL .env'de tanımlı değil." };
  return { title: "Bağlantı hatası", detail: s.length > 120 ? s.slice(0, 120) + "…" : s };
}

export default function Balance() {
  const [symbol, setSymbol] = useState("ETHUSDT");
  const [interval, setInterval] = useState("minute15");

  const { data: bal, refetch, isFetching } = useQuery({
    queryKey: ["balances"], queryFn: fetchBalances, refetchInterval: 30000,
  });
  const { data: meta } = useQuery({ queryKey: ["meta"], queryFn: api.meta });
  const { data: kl } = useQuery({
    queryKey: ["klines", symbol, interval],
    queryFn: () => fetchKlines(symbol, interval),
    refetchInterval: 30000,
  });

  const bars = (kl?.bars || []).map((b) => ({
    t: new Date(b.t * 1000).toLocaleString("tr-TR",
       { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }),
    c: b.c, o: b.o, h: b.h, l: b.l,
  }));

  return (
    <div className="p-5 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-[#ff5d15]" />
            <h2 className="text-sm font-semibold">Bakiye & Grafik</h2>
          </div>
          <p className="text-xs text-zinc-500 mt-0.5">
            Borsalarınızdaki canlı bakiye + işlem grafiği · 30sn&apos;de bir yenilenir
          </p>
        </div>
        <Button data-testid="refresh-balance" onClick={() => refetch()}
          disabled={isFetching}
          className="h-9 px-3 bg-[#1A1A1A] hover:bg-[#27272A] text-zinc-200 rounded-sm">
          <RefreshCw className={`w-4 h-4 mr-1 ${isFetching ? "animate-spin" : ""}`} />
          Yenile
        </Button>
      </div>

      {/* IMPORTANT — Futures-only & manual transfer warning */}
      <div
        data-testid="futures-only-warning"
        className="border-2 border-amber-400/40 bg-gradient-to-r from-amber-500/[0.08] to-amber-400/[0.03] p-4"
      >
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
          <div className="text-sm leading-relaxed">
            <div className="font-semibold text-amber-300 mb-1.5">
              ⚠️ Yalnızca Futures Bakiyesi — Spot Bakiyesi GÖSTERİLMEZ
            </div>
            <p className="text-zinc-300 text-xs">
              Sistem sadece <b className="text-white">Futures cüzdanlarındaki USDT</b>&apos;yi okur ve sadece bu bakiye
              üzerinden pozisyon açar. <b className="text-white">Spot bakiyesi sistem tarafından görülmez</b>.
            </p>
            <p className="text-zinc-400 text-xs mt-1.5">
              Pozisyon açmak için USDT&apos;nizi <b className="text-amber-300">borsanın web/uygulamasından elle</b> Spot → Futures cüzdanına aktarın.
              Hiçbir borsa için sistem üzerinden otomatik transfer yapılmıyor (güvenlik + Darkex API kısıtı).
            </p>
            <div className="flex flex-wrap gap-1.5 mt-2.5">
              <a href="https://www.darkex.com/en/asset/main" target="_blank" rel="noreferrer"
                 className="text-[10px] uppercase tracking-widest text-[#ff5d15] hover:text-[#f0b90b] border border-[#ff5d15]/30 px-2 py-1 bg-[#ff5d15]/[0.05]">
                Darkex Cüzdan ↗
              </a>
              <a href="https://www.binance.com/en/my/dashboard" target="_blank" rel="noreferrer"
                 className="text-[10px] uppercase tracking-widest text-[#F0B90B] hover:text-[#f0b90b] border border-[#F0B90B]/30 px-2 py-1 bg-[#F0B90B]/[0.05]">
                Binance Cüzdan ↗
              </a>
              <a href="https://www.okx.com/account/my-account" target="_blank" rel="noreferrer"
                 className="text-[10px] uppercase tracking-widest text-zinc-300 hover:text-white border border-white/20 px-2 py-1 bg-white/[0.03]">
                OKX Cüzdan ↗
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Total */}
      <div data-testid="total-balance"
        className="bg-gradient-to-br from-[#161616] to-[#0F0F0F] border border-[#27272A] rounded-md p-5">
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
          Toplam Bakiye (USDT)
        </div>
        <div className="font-mono text-4xl font-bold mt-1 text-[#ff5d15] tabular-nums">
          ${(bal?.total_usdt || 0).toLocaleString("en-US",
              { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
        <div className="text-xs text-zinc-500 mt-1">
          {Object.values(bal?.exchanges || {}).filter((x) => !x.error).length} /{" "}
          {Object.keys(bal?.exchanges || {}).length} borsa bağlı
        </div>
      </div>

      {/* Per exchange */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {["darkex", "okx", "binance"].map((key) => {
          const ex = bal?.exchanges?.[key];
          const meta = EXCHANGE_META[key];
          const hasError = !ex || ex.error;
          return (
            <div key={key}
              data-testid={`balance-${key}`}
              className={`bg-[#121212] border rounded-md p-4 transition-colors ${
                hasError ? "border-[#ff5662]/30" : "border-[#2bd98a]/30"
              }`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.16em] font-semibold"
                       style={{ color: meta.color }}>
                    {meta.label}
                  </div>
                  <div className="text-[9px] uppercase tracking-wider text-zinc-500 mt-0.5">
                    {meta.role}
                  </div>
                </div>
                {hasError ? (
                  <AlertCircle className="w-4 h-4 text-[#ff5662]" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 text-[#2bd98a]" />
                )}
              </div>
              {hasError ? (
                <div className="mt-3 space-y-2">
                  <div className="text-[11px] text-[#ff5662] font-medium">
                    {friendlyError(ex?.error).title}
                  </div>
                  <div className="text-[10px] text-zinc-400 leading-relaxed">
                    {friendlyError(ex?.error).detail}
                  </div>
                  <div className="text-[10px] text-zinc-500 leading-relaxed pt-1 border-t border-white/5">
                    {meta.apiHelp}
                  </div>
                  <div className="flex gap-1.5 pt-1">
                    <a
                      href={meta.loginUrl}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`open-${key}-exchange`}
                      className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white border border-white/10 px-2 py-1 hover:bg-white/5 flex items-center gap-1"
                    >
                      Borsaya git <ArrowRight className="w-3 h-3" />
                    </a>
                    <Link
                      to="/settings"
                      data-testid={`fix-${key}-key`}
                      className="text-[10px] uppercase tracking-widest text-amber-300 hover:text-amber-200 border border-amber-400/30 bg-amber-400/[0.05] px-2 py-1 hover:bg-amber-400/[0.1] flex items-center gap-1"
                    >
                      <KeyRound className="w-3 h-3" /> Anahtarı gir
                    </Link>
                  </div>
                </div>
              ) : (
                <>
                  <div className="mt-3 font-mono text-2xl font-semibold tracking-tight tabular-nums">
                    ${ex.total_usdt.toFixed(2)}
                  </div>
                  <div className="mt-2 text-[11px] text-zinc-500 space-y-0.5">
                    {(ex.balances || []).slice(0, 5).map((b) => (
                      <div key={b.asset} className="flex justify-between font-mono">
                        <span>{b.asset}</span>
                        <span>{b.total.toFixed(4)}{b.locked > 0 && ` (${b.locked.toFixed(2)} kilitli)`}</span>
                      </div>
                    ))}
                  </div>
                  {/* Transfer buttons removed — user must transfer manually via exchange web/app */}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Chart */}
      <div className="bg-[#121212] border border-[#27272A] rounded-md">
        <div className="px-4 py-3 border-b border-[#1A1A1A] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#0A84FF]" />
            <h3 className="text-sm font-semibold">{symbol} · {interval}</h3>
            <span className="text-xs text-zinc-500">· Binance verisi</span>
          </div>
          <div className="flex items-center gap-2">
            <Select value={symbol} onValueChange={setSymbol}>
              <SelectTrigger data-testid="chart-symbol"
                className="bg-transparent border-[#27272A] h-8 rounded-sm w-32 font-mono text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#27272A] rounded-sm text-white">
                {(meta?.pairs || ["ETHUSDT"]).map((p) =>
                  <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={interval} onValueChange={setInterval}>
              <SelectTrigger data-testid="chart-interval"
                className="bg-transparent border-[#27272A] h-8 rounded-sm w-24 font-mono text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#27272A] rounded-sm text-white">
                <SelectItem value="minute5">5dk</SelectItem>
                <SelectItem value="minute15">15dk</SelectItem>
                <SelectItem value="minute30">30dk</SelectItem>
                <SelectItem value="hour1">1s</SelectItem>
                <SelectItem value="hour4">4s</SelectItem>
                <SelectItem value="day1">1g</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="p-3">
          {bars.length > 0 ? (
            <ResponsiveContainer width="100%" height={360}>
              <AreaChart data={bars} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="priceFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0A84FF" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#0A84FF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#1A1A1A" />
                <XAxis dataKey="t" stroke="#71717A"
                  tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}
                  minTickGap={40} />
                <YAxis stroke="#71717A" domain={["auto", "auto"]}
                  tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}
                  tickFormatter={(v) => `$${v.toFixed(0)}`} />
                <Tooltip
                  contentStyle={{ background: "#121212",
                    border: "1px solid #27272A", borderRadius: 4,
                    fontFamily: "JetBrains Mono", fontSize: 12 }}
                  formatter={(v) => `$${Number(v).toFixed(2)}`} />
                <Area type="monotone" dataKey="c" stroke="#0A84FF"
                  strokeWidth={2} fill="url(#priceFill)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[360px] flex items-center justify-center text-zinc-500 text-sm">
              Veri yok — anahtarsız çalışan public endpoint olduğu için her semboldan veri gelmeyebilir.
            </div>
          )}
        </div>
      </div>

      {/* Security note */}
      <div className="bg-[#121212] border border-[#27272A] rounded-md p-3 flex items-start gap-3 text-xs">
        <Shield className="w-4 h-4 text-[#2bd98a] shrink-0 mt-0.5" />
        <div className="text-zinc-400">
          <b className="text-zinc-200">Güvenlik:</b> API anahtarlarımızda <b>Withdraw (çekim) yetkisi yok</b>.
          Sistem sadece bakiye okuma + pozisyon açma/kapama yapar; paranız borsadan dışarı çıkamaz.
        </div>
      </div>
    </div>
  );
}

const TransferDialog = ({ exchange, label }) => {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => api.transferToFutures(exchange, parseFloat(amount)),
    onSuccess: () => {
      toast.success(`${label}: $${amount} USDT Spot → Futures aktarıldı`);
      qc.invalidateQueries({ queryKey: ["balances"] });
      setOpen(false);
      setAmount("");
    },
    onError: (e) =>
      toast.error(errMsg(e, "Transfer hatası")),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid={`transfer-${exchange}-btn`}
          className="w-full mt-3 h-8 bg-[#0A84FF]/10 hover:bg-[#0A84FF]/20 text-[#0A84FF] border border-[#0A84FF]/30 rounded-sm text-[10px] uppercase tracking-widest font-semibold"
        >
          <ArrowRightLeft className="w-3 h-3 mr-1.5" /> Spot → Futures
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-sm rounded-md">
        <DialogHeader>
          <DialogTitle>{label} · Spot → Futures Aktar</DialogTitle>
          <p className="text-xs text-zinc-500 mt-1">
            Spot cüzdanından Futures (USDT-M) cüzdanına USDT aktar.
            <b className="text-emerald-400"> API anahtarı kayıtlı</b> borsalarda çalışır.
          </p>
        </DialogHeader>
        <Input
          data-testid={`transfer-${exchange}-amount`}
          type="number"
          step="0.01"
          placeholder="Tutar (USDT)"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="bg-transparent border-[#27272A] font-mono"
        />
        <div className="text-[10px] text-zinc-500">
          Transfer ücretsizdir. İşlem başarılıysa Futures bakiye anında güncellenir.
        </div>
        <DialogFooter>
          <Button
            data-testid={`transfer-${exchange}-submit`}
            onClick={() => mut.mutate()}
            disabled={!amount || mut.isPending}
            className="bg-[#0A84FF] hover:bg-[#0A84FF]/90 text-white font-semibold rounded-sm"
          >
            {mut.isPending ? "Aktarılıyor…" : "Aktar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
